import { Trans, useLingui } from '@lingui/react/macro'
import {
  Badge,
  Box,
  Card,
  Group,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
} from '@mantine/core'
import {
  IconAlertTriangle,
  IconCheck,
  IconDoor,
  IconDroplet,
  IconMapPin,
  IconPlayerPlay,
  IconPool,
  IconTemperature,
  IconWind,
  type Icon,
} from '@tabler/icons-react'

interface SensorStatus {
  id: string
  icon: Icon
  label: string
  value: string
  status: 'ok' | 'warning' | 'alert'
}

// Demo placeholder image - Spanish villa
const DEMO_IMAGE =
  'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=800&q=80'

interface PropertyHeroCardProps {
  propertyName?: string
  location?: string
  imageUrl?: string
  sensors?: SensorStatus[]
  showAlert?: boolean
}

export function PropertyHeroCard({
  propertyName = 'Villa Marbella',
  location = 'Costa del Sol, España',
  imageUrl = DEMO_IMAGE,
  sensors,
  showAlert = false,
}: PropertyHeroCardProps) {
  const { t } = useLingui()

  // Demo sensors for Spain villa
  const defaultSensors: SensorStatus[] = [
    {
      id: 'temp',
      icon: IconTemperature,
      label: t`Temperature`,
      value: '23°C',
      status: 'ok',
    },
    {
      id: 'humidity',
      icon: IconWind,
      label: t`Humidity`,
      value: '48%',
      status: 'ok',
    },
    { id: 'pool', icon: IconPool, label: t`Pool`, value: 'OK', status: 'ok' },
    {
      id: 'doors',
      icon: IconDoor,
      label: t`Locking`,
      value: 'OK',
      status: 'ok',
    },
    {
      id: 'water',
      icon: IconDroplet,
      label: t`Water leak`,
      value: 'OK',
      status: 'ok',
    },
  ]
  const activeSensors = sensors || defaultSensors
  // Demo: optionally show alert state
  const displaySensors = showAlert
    ? activeSensors.map((s) =>
        s.id === 'water'
          ? { ...s, value: t`LEAK!`, status: 'alert' as const }
          : s,
      )
    : activeSensors

  const hasAlert = displaySensors.some((s) => s.status === 'alert')
  const allOk = displaySensors.every((s) => s.status === 'ok')

  return (
    <Card
      radius="lg"
      p={0}
      withBorder
      shadow="md"
      style={{ overflow: 'hidden' }}
    >
      {/* Hero image with LIVE badge only */}
      <Box
        h={{ base: 200, md: 440 }}
        style={{
          backgroundImage: `url(${imageUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          position: 'relative',
        }}
      >
        {/* LIVE badge in corner */}
        <Box style={{ position: 'absolute', top: 12, left: 12 }}>
          <LiveBadge />
        </Box>

        {/* Play button in center */}
        <Box
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <ThemeIcon
            size={48}
            radius="xl"
            color="dark"
            style={{
              backgroundColor: 'rgba(0,0,0,0.5)',
              cursor: 'pointer',
            }}
          >
            <IconPlayerPlay size={24} color="white" />
          </ThemeIcon>
        </Box>
      </Box>

      {/* Content below image */}
      <Stack gap="md" p="md">
        {/* Property name & status */}
        <Group justify="space-between" align="flex-start">
          <Stack gap={4}>
            <Text size="lg" fw={600}>
              {propertyName}
            </Text>
            <Group gap={4}>
              <IconMapPin size={14} color="var(--mantine-color-dimmed)" />
              <Text size="sm" c="dimmed">
                {location}
              </Text>
            </Group>
          </Stack>
          <StatusBadge allOk={allOk} hasAlert={hasAlert} />
        </Group>

        {/* Sensor status grid */}
        <SimpleGrid cols={3} spacing="xs">
          {displaySensors.map((sensor) => (
            <SensorBadge key={sensor.id} sensor={sensor} />
          ))}
        </SimpleGrid>
      </Stack>
    </Card>
  )
}

function LiveBadge() {
  return (
    <Badge
      size="lg"
      variant="filled"
      color="red"
      leftSection={
        <Box
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: 'white',
            animation: 'pulse 1.5s infinite',
          }}
        />
      }
      styles={{
        root: {
          textTransform: 'uppercase',
          letterSpacing: 1,
        },
      }}
    >
      <Trans>Live</Trans>
    </Badge>
  )
}

function StatusBadge({
  allOk,
  hasAlert,
}: {
  allOk: boolean
  hasAlert: boolean
}) {
  if (hasAlert) {
    return (
      <Badge
        size="lg"
        variant="filled"
        color="red"
        leftSection={<IconAlertTriangle size={14} />}
      >
        <Trans>Alert!</Trans>
      </Badge>
    )
  }

  if (allOk) {
    return (
      <Badge
        size="lg"
        variant="filled"
        color="green"
        leftSection={<IconCheck size={14} />}
      >
        <Trans>All OK</Trans>
      </Badge>
    )
  }

  return (
    <Badge size="lg" variant="filled" color="yellow">
      <Trans>Check</Trans>
    </Badge>
  )
}

function SensorBadge({ sensor }: { sensor: SensorStatus }) {
  const Icon = sensor.icon
  const isAlert = sensor.status === 'alert'
  const isWarning = sensor.status === 'warning'

  const color = isAlert ? 'red' : isWarning ? 'yellow' : 'green'

  return (
    <Box
      px="sm"
      py="xs"
      style={{
        borderRadius: 'var(--mantine-radius-md)',
        backgroundColor: `var(--mantine-color-${color}-0)`,
        border: `1px solid var(--mantine-color-${color}-2)`,
      }}
    >
      <Group gap={6} wrap="nowrap">
        <ThemeIcon size={20} variant="light" color={color} radius="xl">
          {sensor.status === 'ok' ? (
            <IconCheck size={12} />
          ) : (
            <Icon size={12} />
          )}
        </ThemeIcon>
        <Stack gap={0}>
          <Text size="xs" c="dimmed" lh={1.2}>
            {sensor.label}
          </Text>
          <Text size="sm" fw={600} c={isAlert ? 'red' : undefined} lh={1.2}>
            {sensor.value}
          </Text>
        </Stack>
      </Group>
    </Box>
  )
}
